import { useState, useEffect } from 'react';
import { FileText, Download, Trash2, Eye, Upload, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../core/apiClient';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ExcelFilterDropdown } from '../../components/ui/excel-filter';

interface Document {
  id: number;
  name: string;
  type: string;
  category: string;
  file_path?: string;
  file_name?: string;
  uploadDate: string;
  fileSize: number;
  status: 'active' | 'archived' | 'pending';
}

export default function Documents() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: 'Contracts',
    file: null as File | null,
  });

  useEffect(() => {
    // Cargar documentos
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const response: any = await api.get('/hr-documents');
      if (response.success) {
        setDocuments(response.data || []);
      }
    } catch (error) {
      console.error('Error carregant documents:', error);
    }
  };

  const handleUpload = async () => {
    if (!formData.file || !formData.name) {
      alert('Completa tots els camps');
      return;
    }

    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', formData.file);
      uploadFormData.append('name', formData.name);
      uploadFormData.append('type', formData.category);

      const response: any = await api.post('/hr-documents', uploadFormData);

      if (response.success) {
        setShowModal(false);
        setFormData({ name: '', category: 'Contracts', file: null });
        loadDocuments();
        alert('Document pujat correctament');
      } else {
        alert('Error: ' + (response.message || 'Error desconegut'));
      }
    } catch (error) {
      console.error('Error pujant document:', error);
      alert('Error pujant el document');
    } finally {
      setUploading(false);
    }
  };

  const handleView = (doc: Document) => {
    if (doc.file_path) {
      window.open(doc.file_path, '_blank');
    } else {
      alert('No hi ha fitxer disponible');
    }
  };

  const handleDownload = (doc: Document) => {
    if (doc.file_path) {
      const link = document.createElement('a');
      link.href = doc.file_path;
      link.download = doc.file_name || doc.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      alert('No hi ha fitxer disponible');
    }
  };

  const handleDelete = async (doc: Document) => {
    if (!window.confirm(`Estàs segur que vols eliminar "${doc.name}"?`)) {
      return;
    }

    try {
      const response: any = await api.delete(`/hr-documents/${doc.id}`);
      if (response.success) {
        loadDocuments();
        alert('Document eliminat correctament');
      } else {
        alert('Error: ' + (response.message || 'Error desconegut'));
      }
    } catch (error) {
      console.error('Error eliminant document:', error);
      alert('Error eliminant el document');
    }
  };

  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || filterCategory.split(',').includes(doc.category);
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/rrhh')}
            title="Tornar enrere"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Documents</h1>
            <p className="text-slate-500">Gestió de contractes i documentació laboral</p>
          </div>
        </div>
        <Button onClick={() => setShowModal(true)}>
          <Upload className="w-4 h-4 mr-2" />
          Pujar Document
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-end">
        <div className="flex-1">
          <label className="block text-sm font-medium text-slate-700 mb-2">Cerca</label>
          <Input
            placeholder="Cercar per nom..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="w-48">
          <label className="block text-sm font-medium text-slate-700 mb-2">Categoria</label>
          <ExcelFilterDropdown
            label="Categoria"
            options={[
              { value: 'Contracts', label: 'Contractes' },
              { value: 'Policies', label: 'Polítiques' },
              { value: 'Certifications', label: 'Certificats' },
              { value: 'Other', label: 'Altres' },
            ]}
            selected={filterCategory === 'all' ? [] : filterCategory.split(',')}
            onApply={(values) => setFilterCategory(values.length === 0 ? 'all' : values.join(','))}
            className="w-full"
          />
        </div>
      </div>

      {/* Documents Table */}
      <div className="bg-white rounded-lg border border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Nom</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Categoria</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Data Pujada</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Mida</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">Estat</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-700">Accions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDocuments.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-slate-500">
                    No hi ha documents
                  </td>
                </tr>
              ) : (
                filteredDocuments.map((doc) => (
                  <tr key={doc.id} className="border-b border-slate-200 hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-blue-600" />
                        <span className="font-medium text-slate-900">{doc.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{doc.category}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{new Date(doc.uploadDate).toLocaleDateString('ca-ES')}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{(doc.fileSize / 1024).toFixed(1)} KB</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        doc.status === 'active' ? 'bg-green-100 text-green-800' :
                        doc.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-slate-100 text-slate-800'
                      }`}>
                        {doc.status === 'active' ? 'Actiu' :
                         doc.status === 'pending' ? 'Pendent' :
                         'Arxivat'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          title="Veure"
                          onClick={() => handleView(doc)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          title="Descarregar"
                          onClick={() => handleDownload(doc)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          title="Eliminar" 
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(doc)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-sm font-bold mb-4">Pujar Document</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Nom del document</label>
                <Input
                  placeholder="Ex: Contracte Laboral"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Categoria</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Contracts">Contractes</option>
                  <option value="Policies">Polítiques</option>
                  <option value="Certifications">Certificats</option>
                  <option value="Other">Altres</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Fitxer (màx 5 MB)</label>
                <input
                  type="file"
                  onChange={(e) => setFormData({ ...formData, file: e.target.files?.[0] || null })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {formData.file && <p className="text-sm text-slate-600 mt-1">{formData.file.name}</p>}
              </div>
            </div>

            <div className="flex gap-2 justify-end mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowModal(false);
                  setFormData({ name: '', category: 'Contracts', file: null });
                }}
                disabled={uploading}
              >
                Cancel·lar
              </Button>
              <Button
                onClick={handleUpload}
                disabled={uploading}
              >
                {uploading ? 'Pujant...' : 'Pujar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
